import { useEffect, useState, useMemo } from "react";
import { useLocation } from "react-router-dom";
import { ShoppingBag, X } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Compact "João de SP comprou X há 5 min" toast that cycles through the last
// paid orders. Feature-flagged server-side (see /api/social-proof/recent).
export const SocialProofToast = () => {
  const location = useLocation();
  const [items, setItems] = useState([]);
  const [enabled, setEnabled] = useState(false);
  const [index, setIndex] = useState(0);
  const [visible, setVisible] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  // Never render on the admin dashboard
  const isAdmin = location.pathname.startsWith("/painel");

  // 1. Load feed once
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const resp = await fetch(`${API}/social-proof/recent?limit=12`);
        if (!resp.ok) return;
        const data = await resp.json();
        if (cancelled) return;
        setEnabled(!!data.enabled);
        setItems(Array.isArray(data.items) ? data.items : []);
      } catch (_) {}
    })();
    return () => { cancelled = true; };
  }, []);

  // 2. Cycle through items every ~9s (first toast after 3s)
  useEffect(() => {
    if (!enabled || dismissed || items.length === 0) return;
    const showFirst = setTimeout(() => setVisible(true), 3000);
    const cycle = setInterval(() => {
      setVisible(false);
      setTimeout(() => {
        setIndex((i) => (i + 1) % items.length);
        setVisible(true);
      }, 400);
    }, 9000);
    return () => { clearTimeout(showFirst); clearInterval(cycle); };
  }, [enabled, dismissed, items.length]);

  const current = items[index];
  const timeAgo = useMemo(() => {
    if (!current?.at) return "agora";
    const then = new Date(current.at).getTime();
    if (Number.isNaN(then)) return "agora";
    const mins = Math.max(1, Math.floor((Date.now() - then) / 60000));
    if (mins < 60) return `há ${mins} min`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `há ${hrs} h`;
    const days = Math.floor(hrs / 24);
    return `há ${days} d`;
  }, [current]);

  if (!enabled || dismissed || isAdmin || items.length === 0 || !current) return null;

  return (
    <div
      className={`fixed bottom-4 left-4 z-40 max-w-xs bg-white rounded-xl shadow-lg border border-gray-200 transition-all duration-500 ${
        visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4 pointer-events-none"
      }`}
      data-testid="social-proof-toast"
    >
      <div className="flex items-start gap-3 p-3">
        <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
          <ShoppingBag className="text-emerald-600" size={18} />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm text-gray-900 leading-tight">
            <strong>{current.name}</strong> de <strong>{current.state}</strong> comprou
          </p>
          <p className="text-xs text-gray-600 truncate">{current.product}</p>
          <p className="text-[11px] text-gray-400 mt-0.5">✓ Compra confirmada · {timeAgo}</p>
        </div>
        <button
          type="button"
          aria-label="Fechar"
          className="text-gray-300 hover:text-gray-600 flex-shrink-0"
          onClick={() => setDismissed(true)}
          data-testid="social-proof-dismiss"
        >
          <X size={16} />
        </button>
      </div>
    </div>
  );
};

export default SocialProofToast;
